import React, { useState, useMemo, useRef, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import cloneDeep from 'lodash.clonedeep';
import classes from './Onboarding.module.css';
import OnboardingProgress from './OnboardingProgress';
import { Urls } from '../../Urls';
import { Constants } from '../../Constants';
import LocationAutoComplete from '../Shared/AutoCompleteV2/AutoCompleteV2';
import Dropdown from '../Shared/Forms/Dropdown/Dropdown';
import { FaTrash, FaPlus, FaChevronUp, FaChevronDown, FaCheck } from 'react-icons/fa';
import { MdWarning, MdClose } from 'react-icons/md';
import { RiSparklingFill } from 'react-icons/ri';
import { useCreateTransporterProfile } from '../../hooks/useCreateTransporterProfile';
import { useCheckDuplicateTransporterName } from '../../hooks/useCheckDuplicateTransporterName';
import { calculateProfileCompletion, getCompletionColor } from '../../shared_utilities/profileCompletion';
import { useOnboardingMixpanelTrack } from './hooks/useOnboardingMixpanelTrack';


const CONFIDENCE_THRESHOLD = 0.7;
const OUTER_STEPS = ['Upload Doc', 'Review & Confirm'];

// 3-step left panel labels
const LEFT_STEPS = [
    { label: 'Upload Document', icon: '↑' },
    { label: 'Essential Details', icon: '✦' },
    { label: 'Additional Details', icon: '◉' },
];

const validEmail = e => !e ? false : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const isE164 = n => !!n && /^\+[1-9]\d{1,14}$/.test(n);

// ── Pure helpers ───────────────────────────────────────────────────────────
const getVal = (fields, key) => {
    const f = fields[key];
    if (!f) return null;
    return (typeof f === 'object' && 'value' in f) ? f.value : f;
};

const getConf = (fields, key) => {
    const f = fields[key];
    if (!f || typeof f !== 'object') return null;
    return f.confidence ?? null;
};

const isLowConf = (fields, key) => {
    const c = getConf(fields, key);
    return c !== null && c < CONFIDENCE_THRESHOLD;
};

// Count non-empty extracted fields at mount (fixed — used for AI fill stats)
const countAiFilled = (fields) => {
    let count = 0;
    Object.values(fields).forEach(f => {
        const v = typeof f === 'object' && 'value' in f ? f.value : f;
        if (v === null || v === undefined || v === '') return;
        if (Array.isArray(v)) { if (v.length > 0) count++; return; }
        if (typeof v === 'object') { if (Object.values(v).some(x => x !== null && x !== '')) count++; return; }
        if (String(v).trim()) count++;
    });
    return count;
};

// Returns true if a field had a non-empty value extracted by AI at upload time
const wasAiFilled = (fields, key) => {
    const v = getVal(fields, key);
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
};

// ── Sub-components ────────────────────────────────────────────────────────

// NOTE: SectionToggle, FinRow, and AiInputClear MUST live at module scope (not inside
// OnboardingReview) so React sees stable component identities across renders.
// Defining them inside the parent causes unmount/remount on every keystroke,
// which moves focus out of inputs.

const AiInputClear = ({ show, onClear }) =>
    show ? (
        <button type="button" className={classes.aiClearBtn} onClick={onClear} title="Clear AI-filled value">
            <MdClose size={12} />
        </button>
    ) : null;

const SectionToggle = ({ title, sKey, openSections, toggleSection, hasAiFilled, children }) => (
    <div className={classes.reviewSectionBlock}>
        <button className={classes.reviewSectionToggle} onClick={() => toggleSection(sKey)} type="button">
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                {title}
                {hasAiFilled && (
                    <span className={classes.aiSectionBadge}>
                        <RiSparklingFill size={8} /> AI filled
                    </span>
                )}
            </span>
            {openSections[sKey] ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        {openSections[sKey] && <div className={classes.reviewSectionContent}>{children}</div>}
    </div>
);

const FinRow = ({ label, entry, setEntry, fyOptions }) => (
    <div className={classes.finRow} style={{ marginBottom: 6 }}>
        <div className={classes.finLabel} style={{ flex: '0 0 90px', fontSize: 12 }}>{label}</div>
        <div className={classes.finFields}>
            <div style={{ flex: '0 0 120px' }}>
                <select className={classes.finSelect} value={entry.financial_year} onChange={e => setEntry(prev => ({ ...prev, financial_year: e.target.value }))}>
                    {fyOptions.map(fy => <option key={fy.value} value={fy.value}>{fy.label}</option>)}
                </select>
            </div>
            <input className={`${classes.textInput} ${classes.finAmtInput}`} type="number" placeholder="Amount" value={entry.amount} min="0" onChange={e => setEntry(prev => ({ ...prev, amount: e.target.value }))} />
            <select className={classes.finUnitSelect} value={entry.unit} onChange={e => setEntry(prev => ({ ...prev, unit: e.target.value }))}>
                <option value="CR">₹ CR</option>
                <option value="Lakhs">₹ Lakhs</option>
                <option value="Thousands">$ Thousands</option>
                <option value="Millions">$ Millions</option>
            </select>
        </div>
    </div>
);

const ConfBadge = ({ fields, fieldKey }) => {
    const c = getConf(fields, fieldKey);
    if (c === null) return null;
    const pct = Math.round(c * 100);
    const low = c < CONFIDENCE_THRESHOLD;
    return (
        <span
            className={`${classes.confidenceBadge} ${low ? classes.lowConfidence : classes.highConfidence}`}
            style={{ position: 'static', transform: 'none', marginLeft: 5 }}
        >
            {pct}%
        </span>
    );
};

/** SVG circular completion ring */
const CompletionRing = ({ pct, color, size = 68 }) => {
    const r = (size - 8) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    return (
        <svg width={size} height={size} style={{ display: 'block' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eceaf4" strokeWidth={6} />
            <circle
                cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={color} strokeWidth={6}
                strokeDasharray={`${dash} ${circ}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dasharray 0.4s ease' }}
            />
            <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
                fontSize={13} fontWeight={700} fill={color}>
                {pct}%
            </text>
        </svg>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const OnboardingReview = ({ fields = {}, transporterData, onSuccess, onBack, onSwitchPath }) => {

    // Fixed AI fill count (computed once at mount)
    const aiFilledCount = useMemo(() => countAiFilled(fields), []); // eslint-disable-line

    // ── STEP 1: Mandatory fields ─────────────────────────────────────────

    const [companyName, setCompanyName] = useState(getVal(fields, 'transporter_name') || '');
    const { checkDuplicate } = useCheckDuplicateTransporterName();
    const [isDuplicateName, setIsDuplicateName] = useState(false);
    const companyNameDebounceRef = useRef(null);
    const [headquarter, setHeadquarter] = useState(getVal(fields, 'headquarter') || '');
    const [companyType, setCompanyType] = useState(() => {
        const raw = getVal(fields, 'company_type') || '';
        const opts = Constants.CompanyTypes || [];
        return opts.find(o => o.value.toLowerCase() === raw.toLowerCase())?.value || raw;
    });

    const zoneOptions = Array.isArray(Constants?.AreaOfOperation) ? cloneDeep(Constants.AreaOfOperation) : [];
    const [zone, setZone] = useState(() => {
        const raw = getVal(fields, 'zone') || getVal(fields, 'area_of_operation') || [];
        const arr = Array.isArray(raw) ? raw : [String(raw)];
        return arr.map(z => {
            const opt = zoneOptions.find(o => o.value.toLowerCase() === z.toLowerCase() || o.label.toLowerCase() === z.toLowerCase());
            return opt ? opt.value : null;
        }).filter(Boolean);
    });

    const reviewBodyRef = useRef(null);

    // ── Error-field refs (for scroll-to-error) ────────────────────────────
    // Step 1
    const companyNameRef = useRef(null);
    const headquarterRef = useRef(null);
    const zoneRef        = useRef(null);
    const pocNameRef     = useRef(null);
    const pocEmailRef    = useRef(null);
    const modesRef       = useRef(null);
    const trucksRef      = useRef(null);

    // Step 2
    const extraContactsRef    = useRef(null);   // scrolls to the whole extra-contacts block
    const extraContactRowRefs = useRef([]);      // per-row refs so we can jump to the first bad row

    const systemContact = transporterData?.contact_information?.[0] || {};
    const extractedContacts = Array.isArray(getVal(fields, 'contact_information'))
        ? getVal(fields, 'contact_information') : [];
    const firstExtracted = extractedContacts[0] || {};
    const [pocName, setPocName] = useState(firstExtracted.name || '');
    const [pocEmail, setPocEmail] = useState(firstExtracted.email || '');

    const transportationModes = Constants.TransportationModes || [];
    const [selectedModes, setSelectedModes] = useState(() => {
        const extracted = getVal(fields, 'modes_of_transportation') || [];
        return (Array.isArray(extracted) ? extracted : [])
            .map(m => m?.name).filter(n => transportationModes.some(tm => tm.name === n));
    });

    const [trucks, setTrucks] = useState(() => {
        const raw = getVal(fields, 'truck_asset_details') || [];
        return Array.isArray(raw) && raw.length
            ? raw.map(t => ({ type: t.type || '', self_owned: '', avg_fleet_age: '', planned_increase: '', attached_vehicles: '', vendors: '' }))
            : [{ type: '', self_owned: '', avg_fleet_age: '', planned_increase: '', attached_vehicles: '', vendors: '' },
            { type: '', self_owned: '', avg_fleet_age: '', planned_increase: '', attached_vehicles: '', vendors: '' }];
    });

    // ── STEP 2: Extracted optional fields ────────────────────────────────
    const fyOptions = cloneDeep(Constants.FinancialYears || []);
    const defaultFy = fyOptions[1]?.value ?? '2024-2025';
    const companyTypeOptions = cloneDeep(Constants.CompanyTypes || []);

    const [website, setWebsite] = useState(String(getVal(fields, 'website') || ''));
    const [established, setEstablished] = useState(String(getVal(fields, 'established') || ''));
    const [experienceYear, setExperienceYear] = useState(String(getVal(fields, 'experience_year') || ''));
    const [lspEmail, setLspEmail] = useState(String(getVal(fields, 'lsp_email') || ''));
    const [aboutUs, setAboutUs] = useState(String(getVal(fields, 'about_us') || ''));
    const [hqAddress, setHqAddress] = useState(String(getVal(fields, 'hq_address') || ''));
    const [panNumber, setPanNumber] = useState(String(getVal(fields, 'pan_number') || ''));
    const [cinNumber, setCinNumber] = useState(String(getVal(fields, 'cin_number') || ''));
    const [domainBusiness, setDomainBusiness] = useState(() => {
        const raw = getVal(fields, 'domain_business') || [];
        return Array.isArray(raw) ? raw.join(', ') : String(raw);
    });

    const rawOwned = getVal(fields, 'owned_fleet') || {};
    const [ownedFleet, setOwnedFleet] = useState(typeof rawOwned === 'object' ? (rawOwned.amount || '') : String(rawOwned));
    const [ownedFleetList, setOwnedFleetList] = useState(typeof rawOwned === 'object' ? (rawOwned.fleet_list || '') : '');
    const rawAssoc = getVal(fields, 'associated_fleet') || {};
    const [associatedFleet, setAssociatedFleet] = useState(typeof rawAssoc === 'object' ? (rawAssoc.amount || '') : String(rawAssoc));

    const rawTurnover = getVal(fields, 'turnover') || [];
    const [turnover, setTurnover] = useState(
        Array.isArray(rawTurnover) && rawTurnover.length
            ? rawTurnover.slice(0, 3).map(t => ({ financial_year: t.financial_year || defaultFy, amount: t.amount || '', unit: t.unit || 'CR' }))
            : [{ financial_year: defaultFy, amount: '', unit: 'CR' }]
    );

    const rawNetProfit = getVal(fields, 'net_profit') || [];
    const [netProfit, setNetProfit] = useState(
        Array.isArray(rawNetProfit) && rawNetProfit.length
            ? rawNetProfit.slice(0, 3).map(t => ({ financial_year: t.financial_year || defaultFy, amount: t.amount || '', unit: t.unit || 'CR' }))
            : [{ financial_year: defaultFy, amount: '', unit: 'CR' }]
    );

    // Extra contacts — mandatory if partially filled
    const [extraContacts, setExtraContacts] = useState(() =>
        extractedContacts.slice(1)
            .filter(c => c.name || c.email || c.contact_number)
            .map(c => ({ name: c.name || '', email: c.email || '', contact_number: c.contact_number || '', designation: c.designation || '' }))
    );
    const [contactErrors, setContactErrors] = useState([]); // per-row errors

    // ── UI state ──────────────────────────────────────────────────────────
    const [reviewStep, setReviewStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [openSections, setOpenSections] = useState({ company: true, operations: false, fleet: false, financials: false });
    const toggleSection = key => {
        const willOpen = !openSections[key];
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
        trackOnboardingEvent(
            'onboarding review',
            'Review section toggled',
            { section_key: key, opened: willOpen }
        );
    };

    // ── Unified submit hook ───────────────────────────────────────────────
    const { createProfile, saving, error: saveErr } = useCreateTransporterProfile();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    // Track review screen mount
    useEffect(() => {
        trackOnboardingEvent(
            'onboarding review',
            'Review screen opened',
            { ai_filled_count: aiFilledCount, low_conf_count: lowCount }
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Truck helpers ─────────────────────────────────────────────────────
    const addTruck = () => setTrucks(p => [...p, { type: '', self_owned: '', avg_fleet_age: '', planned_increase: '', attached_vehicles: '', vendors: '' }]);
    const removeTruck = i => setTrucks(p => p.filter((_, idx) => idx !== i));
    const updateTruck = (i, val) => setTrucks(prev => prev.map((t, idx) => idx === i ? {
        ...t, type: val, self_owned: '', avg_fleet_age: '', planned_increase: '', attached_vehicles: '', vendors: '',
    } : t));

    const toggleMode = name => {
        setSelectedModes(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
        setErrors(p => ({ ...p, modes: '' }));
    };

    // ── Live profile completion (computed every render from current state) ─
    const liveProfile = useMemo(() => ({
        transporter_name: companyName,
        headquarter,
        zone,
        company_type: companyType,
        website,
        lsp_email: lspEmail,
        established,
        experience_year: experienceYear,
        about_us: aboutUs,
        pan_number: panNumber,
        cin_number: cinNumber,
        domain_business: domainBusiness,
        modes_of_transportation: selectedModes.map(n => ({ name: n })),
        truck_asset_details: trucks.filter(t => t.type.trim()),
        owned_fleet: ownedFleet ? { amount: ownedFleet } : null,
        associated_fleet: associatedFleet ? { amount: associatedFleet } : null,
        turnover: turnover.filter(t => t.amount),
        net_profit: netProfit.filter(t => t.amount),
        contact_information: [pocName ? { name: pocName, email: pocEmail } : null, ...extraContacts].filter(Boolean),
    }), [
        companyName, headquarter, zone, companyType, website, lspEmail, established,
        experienceYear, aboutUs, panNumber, cinNumber, domainBusiness, selectedModes,
        trucks, ownedFleet, associatedFleet, turnover, netProfit, pocName, pocEmail, extraContacts,
    ]);

    const { percentage: livePercent } = calculateProfileCompletion(liveProfile);
    const ringColor = getCompletionColor(livePercent);

    const handleCompanyNameChange = (value) => {
        setCompanyName(value);

        if (!value.trim()) {
            setIsDuplicateName(false);
            setErrors(prev => ({ ...prev, companyName: '' }));
            return;
        }

        if (companyNameDebounceRef.current) {
            clearTimeout(companyNameDebounceRef.current);
        }

        companyNameDebounceRef.current = setTimeout(async () => {
            try {
                const isDuplicate = await checkDuplicate(value.trim());
                setIsDuplicateName(isDuplicate);
                setErrors(prev => ({
                    ...prev,
                    companyName: isDuplicate ? 'This transporter name already exists' : ''
                }));
            } catch (err) {
                console.error('Duplicate check failed', err);
            }
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (companyNameDebounceRef.current) {
                clearTimeout(companyNameDebounceRef.current);
            }
        };
    }, []);

    // Extracted company name bypasses handleCompanyNameChange, so check it once on mount
    useEffect(() => {
        const trimmedName = companyName.trim();
        if (!trimmedName) return;

        (async () => {
            try {
                const isDuplicate = await checkDuplicate(trimmedName);
                setIsDuplicateName(isDuplicate);
                setErrors(prev => ({
                    ...prev,
                    companyName: isDuplicate ? 'This transporter name already exists' : ''
                }));
            } catch (err) {
                console.error('Duplicate check failed', err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Validation ────────────────────────────────────────────────────────
    const validateStep1 = () => {
        const errs = {};
        if (!companyName.trim()) errs.companyName = 'Company name is required';
        if (isDuplicateName) errs.companyName = 'This transporter name already exists';
        if (!headquarter.trim()) errs.headquarter = 'Registered office is required';
        if (!zone.length) errs.zone = 'Select at least one area of operation';
        if (!pocName.trim()) errs.pocName = 'Contact name is required';
        if (!validEmail(pocEmail)) errs.pocEmail = 'Valid email is required';
        if (!selectedModes.length) errs.modes = 'Select at least one mode of transportation';
        const emptyTruck = trucks.find(t => !t.type.trim());
        if (emptyTruck) errs.trucks = 'All truck type entries must be filled';
        setErrors(errs);

        // Scroll to the first invalid field so users can see what to fix
        if (Object.keys(errs).length) {
            const refMap = {
                companyName: companyNameRef,
                headquarter: headquarterRef,
                zone:        zoneRef,
                pocName:     pocNameRef,
                pocEmail:    pocEmailRef,
                modes:       modesRef,
                trucks:      trucksRef,
            };
            const firstKey = Object.keys(errs)[0];
            const target = refMap[firstKey]?.current;
            if (target) {
                setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
            }
        }

        return !Object.keys(errs).length;
    };

    const validateContacts = () => {
        const rowErrors = extraContacts.map(c => {
            const anyFilled = c.name || c.email || c.contact_number;
            if (!anyFilled) return null; // fully empty rows are fine (will be filtered)
            const rowErr = {};
            if (!c.name.trim()) rowErr.name = 'Required';
            if (!validEmail(c.email)) rowErr.email = 'Valid email required';
            if (!c.contact_number.trim()) rowErr.contact_number = 'Required';
            return Object.keys(rowErr).length ? rowErr : null;
        });
        setContactErrors(rowErrors);

        // Scroll to the first row with errors
        const firstErrIdx = rowErrors.findIndex(e => e !== null);
        if (firstErrIdx !== -1) {
            const rowEl = extraContactRowRefs.current[firstErrIdx];
            if (rowEl) {
                setTimeout(() => rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
            } else if (extraContactsRef.current) {
                // Fallback: scroll to the section header if the row ref isn't set yet
                setTimeout(() => extraContactsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
            }
        }

        return rowErrors.every(e => e === null);
    };

    const handleNext = async () => {
        const trimmedName = companyName.trim();
        if (trimmedName) {
            if (companyNameDebounceRef.current) {
                clearTimeout(companyNameDebounceRef.current);
            }
            try {
                const isDuplicate = await checkDuplicate(trimmedName);
                setIsDuplicateName(isDuplicate);
                setErrors(prev => ({
                    ...prev,
                    companyName: isDuplicate ? 'This transporter name already exists' : ''
                }));
                if (isDuplicate) {
                    setTimeout(() => companyNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
                    return;
                }
            } catch (err) {
                console.error('Duplicate check failed', err);
            }
        }

        if (validateStep1()) {
            trackOnboardingEvent(
                'onboarding review',
                'Essential details confirmed — moved to Additional',
                { company_name: companyName, modes_count: selectedModes.length }
            );
            setReviewStep(1);
            reviewBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // ── Build payload ─────────────────────────────────────────────────────
    const buildPayload = () => {
        const base = { ...transporterData };
        base.transporter_name = companyName.trim();
        base.headquarter = headquarter.trim();
        base.hq_address = hqAddress.trim();
        base.zone = zone;
        base.company_type = companyType;
        base.website = website.trim();
        base.lsp_email = lspEmail.trim();
        base.established = established.trim();
        base.experience_year = experienceYear;
        base.about_us = aboutUs.trim();
        base.pan_number = panNumber.trim();
        base.cin_number = cinNumber.trim();
        base.domain_business = domainBusiness ? domainBusiness.split(',').map(s => s.trim()).filter(Boolean) : [];

        base.modes_of_transportation = transportationModes
            .filter(tm => selectedModes.includes(tm.name))
            .map(tm => ({ name: tm.name, values: [] }));

        base.truck_asset_details = trucks.filter(t => t.type.trim()).map(t => ({ ...t, type: t.type.trim() }));

        if (ownedFleet) base.owned_fleet = { amount: ownedFleet, fleet_list: ownedFleetList, financial_year: defaultFy };
        if (associatedFleet) base.associated_fleet = { amount: associatedFleet, financial_year: defaultFy };

        const filledTurnover = turnover.filter(t => t.amount);
        if (filledTurnover.length) base.turnover = filledTurnover.map(t => ({ ...t, currency: ['Thousands', 'Millions'].includes(t.unit) ? '$' : '₹' }));
        const filledNetProfit = netProfit.filter(t => t.amount);
        if (filledNetProfit.length) base.net_profit = filledNetProfit.map(t => ({ ...t, currency: ['Thousands', 'Millions'].includes(t.unit) ? '$' : '₹' }));

        base.contact_information = [
            {
                name: pocName.trim(),
                contact_number: systemContact?.contact_number || '',
                country_code: systemContact?.country_code || 'IN',
                email: pocEmail.trim(),
                designation: '',
                poc: 'yes',
            },
            ...extraContacts
                .filter(c => c.name || c.email || c.contact_number)
                .map(c => ({ name: c.name.trim(), email: c.email.trim(), contact_number: c.contact_number.trim(), designation: c.designation.trim() })),
        ];

        const passthrough = [
            'sectors', 'industry_served', 'value_added_services', 'warehousing_services',
            'customer_reference', 'top_5_companies', 'other_business_interest',
            'shipment_tracking_method', 'tms_experience', 'bank_details', 'gst',
            'manpower_details', 'acceptable_terms', 'authSig_details', 'company_act', 'market_intelligence',
        ];
        passthrough.forEach(key => {
            const v = getVal(fields, key);
            if (v !== null && v !== undefined) base[key] = v;
        });

        base.modes_of_operation = [
            { name: 'Contracted', percentage: '' },
            { name: 'Spot/Walkin/Adhoc', percentage: '' },
        ];
        return base;
    };

    const handleSubmit = async () => {
        if (!validateContacts()) return;
        const payload = buildPayload();
        const { success, message, value } = await createProfile(payload);
        if (success) {
            trackOnboardingEvent(
                'onboarding review',
                'New transporter profile created via AI Review',
                {
                    transporter_id: payload.transporter_id,
                    ai_filled_count: aiFilledCount,
                    modes_count: selectedModes.length,
                    trucks_count: trucks.filter(t => t.type.trim()).length,
                }
            );
            onSuccess(payload);
        } else {
            trackOnboardingEvent(
                'onboarding review',
                'Transporter profile creation failed in Review',
                { error: message }
            );

            // If the backend flagged a duplicate contact number, surface it on the matching row
            if (message === 'Contact number already exists' && value) {
                const rowIdx = extraContacts.findIndex(c => c.contact_number.trim() === value);
                if (rowIdx !== -1) {
                    setContactErrors(prev => {
                        const next = [...prev];
                        next[rowIdx] = { ...(next[rowIdx] || {}), contact_number: 'This phone number already exists' };
                        return next;
                    });
                    const rowEl = extraContactRowRefs.current[rowIdx];
                    if (rowEl) {
                        setTimeout(() => rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
                    }
                }
            }
        }
    };

    // ── Low-confidence summary ────────────────────────────────────────────
    const mandatoryKeys = ['transporter_name', 'headquarter', 'zone', 'modes_of_transportation', 'truck_asset_details', 'contact_information'];
    const lowCount = mandatoryKeys.filter(k => isLowConf(fields, k)).length;

    // ── AI-fill indicators for section header badges ─────────────────────
    const aiCompany = ['website', 'lsp_email', 'established', 'experience_year', 'hq_address', 'about_us', 'domain_business'].some(k => wasAiFilled(fields, k));
    const aiOperations = ['pan_number', 'cin_number'].some(k => wasAiFilled(fields, k));
    const aiFleet = ['owned_fleet', 'associated_fleet'].some(k => wasAiFilled(fields, k));
    const aiFinancials = ['turnover', 'net_profit'].some(k => wasAiFilled(fields, k));

    // ════════════════════════════════════════════════════════════════════════
    return (
        <div className={classes.onboardingWrap}>
            <div className={classes.splitCard}>

                {/* ═══ LEFT PANEL ═══ */}
                <div className={classes.leftPanel}>
                    <div className={classes.logoWrap}>
                        <img src={Urls.LorriLogoLogin} alt="LoRRI" className={classes.logo} />
                    </div>
                    <p className={classes.subheading} style={{ marginBottom: 0 }}>Your profile is being set up</p>

                    {/* Live completion ring */}
                    <div className={classes.reviewCompletionWrap}>
                        <CompletionRing pct={livePercent} color={ringColor} size={72} />
                        <div className={classes.reviewCompletionText}>
                            <div className={classes.reviewCompletionLabel}>Profile completion</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                <RiSparklingFill style={{ color: '#393185', fontSize: 12 }} />
                                <span className={classes.reviewAiFillText}>
                                    AI pre-filled <strong>{aiFilledCount}</strong> fields
                                </span>
                            </div>
                        </div>
                    </div>

                    {lowCount > 0 && (
                        <div className={classes.reviewLeftWarning}>
                            <MdWarning style={{ flexShrink: 0 }} />
                            <span>{lowCount} mandatory field{lowCount > 1 ? 's need' : ' needs'} verification.</span>
                        </div>
                    )}

                    {/* 3-step flow tracker */}
                    <div className={classes.reviewLeftSteps}>
                        {LEFT_STEPS.map((s, i) => {
                            // Step 0 (Upload) is always done
                            // Step 1 (Essential) = done when reviewStep > 0, active when reviewStep === 0
                            // Step 2 (Additional) = active when reviewStep === 1
                            const isDone = i === 0 || (i === 1 && reviewStep > 0);
                            const isActive = (i === 1 && reviewStep === 0) || (i === 2 && reviewStep === 1);
                            return (
                                <div
                                    key={s.label}
                                    className={`${classes.reviewLeftStep} ${isActive ? classes.reviewLeftStepActive : ''} ${isDone ? classes.reviewLeftStepDone : ''}`}
                                    onClick={() => {
                                        if (i === 1 && reviewStep === 1) setReviewStep(0); // allow going back to step 1
                                    }}
                                    style={{ cursor: i === 1 && reviewStep === 1 ? 'pointer' : 'default' }}
                                >
                                    <div className={classes.reviewLeftStepDot}>
                                        {isDone ? <FaCheck size={9} /> : i + 1}
                                    </div>
                                    <span>{s.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ═══ RIGHT PANEL ═══ */}
                <div className={classes.rightPanel} style={{ display: 'flex', flexDirection: 'column' }}>
                    <OnboardingProgress steps={OUTER_STEPS} current={1} />

                    {/* ─── Scrollable body ─── */}
                    <div className={classes.reviewBody} style={{ flex: 1 }} ref={reviewBodyRef}>

                        {/* ════ STEP 1: Essential / Mandatory ════ */}
                        {reviewStep === 0 && (
                            <>
                                <h2 className={classes.heading}>Essential Details</h2>
                                <p className={classes.subheading} style={{ marginBottom: 12 }}>
                                    Required to create your profile. Pre-filled where extracted.
                                </p>

                                <div className={classes.fieldGrid}>
                                    {/* Company Name */}
                                    <div className={`${classes.fieldWrap} ${classes.fieldFull}`} ref={companyNameRef}>
                                        <div className={classes.fieldLabel}>
                                            <span className={classes.asterisk}>*</span>Company Name
                                            <ConfBadge fields={fields} fieldKey="transporter_name" />
                                        </div>
                                        <div className={classes.aiFieldWrap}>
                                            <input
                                                className={`${classes.textInput} ${errors.companyName ? classes.invalid : ''}${wasAiFilled(fields, 'transporter_name') && companyName ? ` ${classes.aiClearInput}` : ''}`}
                                                value={companyName}
                                                onChange={e => handleCompanyNameChange(e.target.value)}
                                                placeholder="Your company name" id="rv-company-name" />
                                            <AiInputClear show={wasAiFilled(fields, 'transporter_name') && !!companyName} onClear={() => handleCompanyNameChange('')} />
                                        </div>
                                        {errors.companyName && <div className={classes.errorMsg}>{errors.companyName}</div>}
                                    </div>

                                    {/* HQ */}
                                    <div className={classes.fieldWrap} ref={headquarterRef}>
                                        <div className={classes.fieldLabel}>
                                            <span className={classes.asterisk}>*</span>Registered Office / HQ
                                            <ConfBadge fields={fields} fieldKey="headquarter" />
                                        </div>
                                        <LocationAutoComplete
                                            value={headquarter ? { label: headquarter, value: headquarter } : null}
                                            onChange={sel => { setHeadquarter(sel ? sel.value : ''); setErrors(p => ({ ...p, headquarter: '' })); }}
                                            onClear={() => { setHeadquarter(''); setErrors(p => ({ ...p, headquarter: '' })); }}
                                            placeholder="Search city"
                                        />
                                        {errors.headquarter && <div className={classes.errorMsg}>{errors.headquarter}</div>}
                                    </div>

                                    {/* Company Type */}
                                    <div className={classes.fieldWrap}>
                                        <div className={classes.fieldLabel}>
                                            Company Type
                                            <ConfBadge fields={fields} fieldKey="company_type" />
                                        </div>
                                        <Dropdown
                                            picklistOptions={companyTypeOptions}
                                            defaultValues={companyTypeOptions.filter(o => o.value === companyType)}
                                            onValueChange={sel => setCompanyType(sel?.value || '')}
                                            placeholder="Select type" isSearchable
                                        />
                                    </div>

                                    {/* Zone */}
                                    <div className={`${classes.fieldWrap} ${classes.fieldFull}`} ref={zoneRef}>
                                        <div className={classes.fieldLabel}>
                                            <span className={classes.asterisk}>*</span>Area of Operation
                                            <ConfBadge fields={fields} fieldKey="zone" />
                                        </div>
                                        <Dropdown
                                            picklistOptions={zoneOptions}
                                            defaultValues={zoneOptions.filter(o => zone.includes(o.value))}
                                            onValueChange={sel => {
                                                setZone(Array.isArray(sel) ? sel.map(s => s?.value) : (sel && Object.keys(sel).length ? [sel.value] : []));
                                                setErrors(p => ({ ...p, zone: '' }));
                                            }}
                                            placeholder="Select regions" isMultiselect isSearchable isNewOptionValid invalid={!!errors.zone}
                                        />
                                        {errors.zone && <div className={classes.errorMsg}>{errors.zone}</div>}
                                    </div>
                                </div>

                                {/* POC */}
                                <div className={classes.reviewSubLabel} style={{ marginTop: 14, marginBottom: 4 }}>
                                    <RiSparklingFill style={{ color: '#393185' }} />
                                    Primary Contact Person
                                    <ConfBadge fields={fields} fieldKey="contact_information" />
                                </div>
                                <div className={classes.fieldGrid}>
                                    <div className={classes.fieldWrap} ref={pocNameRef}>
                                        <div className={classes.fieldLabel}><span className={classes.asterisk}>*</span>Full Name</div>
                                        <div className={classes.aiFieldWrap}>
                                            <input
                                                className={`${classes.textInput} ${errors.pocName ? classes.invalid : ''}${wasAiFilled(fields, 'contact_information') && pocName ? ` ${classes.aiClearInput}` : ''}`}
                                                value={pocName}
                                                onChange={e => { setPocName(e.target.value); setErrors(p => ({ ...p, pocName: '' })); }}
                                                placeholder="Contact person name" id="rv-poc-name" />
                                            <AiInputClear show={wasAiFilled(fields, 'contact_information') && !!pocName} onClear={() => setPocName('')} />
                                        </div>
                                        {errors.pocName && <div className={classes.errorMsg}>{errors.pocName}</div>}
                                    </div>
                                    <div className={classes.fieldWrap} ref={pocEmailRef}>
                                        <div className={classes.fieldLabel}><span className={classes.asterisk}>*</span>Email</div>
                                        <div className={classes.aiFieldWrap}>
                                            <input
                                                className={`${classes.textInput} ${errors.pocEmail ? classes.invalid : ''}${wasAiFilled(fields, 'contact_information') && pocEmail ? ` ${classes.aiClearInput}` : ''}`}
                                                value={pocEmail}
                                                onChange={e => { setPocEmail(e.target.value); setErrors(p => ({ ...p, pocEmail: '' })); }}
                                                placeholder="email@company.com" type="email" id="rv-poc-email" />
                                            <AiInputClear show={wasAiFilled(fields, 'contact_information') && !!pocEmail} onClear={() => setPocEmail('')} />
                                        </div>
                                        {errors.pocEmail && <div className={classes.errorMsg}>{errors.pocEmail}</div>}
                                    </div>
                                    <div className={`${classes.fieldWrap} ${classes.fieldFull}`}>
                                        <div className={classes.fieldLabel}>Phone <span style={{ color: '#9390c0', fontSize: 11 }}>(from your account)</span></div>
                                        <div className={classes.phoneWrapper}>
                                            <PhoneInput international defaultCountry={systemContact?.country_code || 'IN'} value={isE164(systemContact?.contact_number) ? systemContact.contact_number : undefined} disabled />
                                        </div>
                                    </div>
                                </div>

                                {/* Modes */}
                                <div className={classes.reviewSubLabel} style={{ marginTop: 14, marginBottom: 4 }} ref={modesRef}>
                                    <span className={classes.asterisk}>*</span> Modes of Transportation
                                    <ConfBadge fields={fields} fieldKey="modes_of_transportation" />
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                                    {transportationModes.map(tm => {
                                        const sel = selectedModes.includes(tm.name);
                                        return (
                                            <span key={tm.name} onClick={() => toggleMode(tm.name)} style={{
                                                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                                cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                                                border: `1px solid ${sel ? '#393185' : '#dddaf0'}`,
                                                background: sel ? '#393185' : '#faf9fe',
                                                color: sel ? '#fff' : '#3d3a55',
                                            }}>
                                                {tm.name}
                                            </span>
                                        );
                                    })}
                                </div>
                                {errors.modes && <div className={classes.errorMsg}>{errors.modes}</div>}

                                {/* Trucks */}
                                <div className={classes.reviewSubLabel} style={{ marginTop: 14, marginBottom: 4 }} ref={trucksRef}>
                                    <span className={classes.asterisk}>*</span> Fleet / Truck Types
                                    <ConfBadge fields={fields} fieldKey="truck_asset_details" />
                                </div>
                                <div className={classes.trucksContainer}>
                                    {trucks.map((truck, i) => (
                                        <div className={classes.truckRow} key={i}>
                                            <input
                                                className={`${classes.textInput} ${classes.truckRowInput} ${errors.trucks && !truck.type.trim() ? classes.invalid : ''}`}
                                                placeholder="e.g. 20ft Container, Trailer, Mini Truck"
                                                value={truck.type}
                                                onChange={e => updateTruck(i, e.target.value)}
                                                id={`rv-truck-${i}`}
                                            />
                                            {trucks.length > 1 && (
                                                <button className={classes.truckRowRemove} onClick={() => removeTruck(i)} type="button" title="Remove"><FaTrash /></button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {errors.trucks && <div className={classes.errorMsg}>{errors.trucks}</div>}
                                <button className={classes.addTruckBtn} onClick={addTruck} type="button" style={{ marginTop: 6 }}>
                                    <FaPlus /> <span>Add vehicle type</span>
                                </button>
                            </>
                        )}

                        {/* ════ STEP 2: All Extracted Info (Editable) ════ */}
                        {reviewStep === 1 && (
                            <>
                                <h2 className={classes.heading}>Review Extracted Info</h2>
                                <p className={classes.subheading} style={{ marginBottom: 10 }}>
                                    AI filled these from your document. Edit anything before saving.
                                </p>

                                {/* Company Info */}
                                <SectionToggle title="Company Information" sKey="company" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiCompany}>
                                    <div className={classes.fieldGrid}>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>Website <ConfBadge fields={fields} fieldKey="website" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'website') && website ? ` ${classes.aiClearInput}` : ''}`} value={website} onChange={e => setWebsite(e.target.value)} placeholder="www.yourcompany.com" id="rv-website" />
                                                <AiInputClear show={wasAiFilled(fields, 'website') && !!website} onClear={() => setWebsite('')} />
                                            </div>
                                        </div>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>Company Email <ConfBadge fields={fields} fieldKey="lsp_email" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'lsp_email') && lspEmail ? ` ${classes.aiClearInput}` : ''}`} type="email" value={lspEmail} onChange={e => setLspEmail(e.target.value)} placeholder="info@yourcompany.com" />
                                                <AiInputClear show={wasAiFilled(fields, 'lsp_email') && !!lspEmail} onClear={() => setLspEmail('')} />
                                            </div>
                                        </div>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>Established Year <ConfBadge fields={fields} fieldKey="established" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'established') && established ? ` ${classes.aiClearInput}` : ''}`} value={established} onChange={e => setEstablished(e.target.value)} placeholder="e.g. 2005" />
                                                <AiInputClear show={wasAiFilled(fields, 'established') && !!established} onClear={() => setEstablished('')} />
                                            </div>
                                        </div>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>Years of Experience <ConfBadge fields={fields} fieldKey="experience_year" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'experience_year') && experienceYear ? ` ${classes.aiClearInput}` : ''}`} type="number" value={experienceYear} onChange={e => setExperienceYear(e.target.value)} placeholder="e.g. 20" />
                                                <AiInputClear show={wasAiFilled(fields, 'experience_year') && !!experienceYear} onClear={() => setExperienceYear('')} />
                                            </div>
                                        </div>
                                        <div className={`${classes.fieldWrap} ${classes.fieldFull}`}>
                                            <div className={classes.fieldLabel}>HQ Full Address <ConfBadge fields={fields} fieldKey="hq_address" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'hq_address') && hqAddress ? ` ${classes.aiClearInput}` : ''}`} value={hqAddress} onChange={e => setHqAddress(e.target.value)} placeholder="Full registered address" />
                                                <AiInputClear show={wasAiFilled(fields, 'hq_address') && !!hqAddress} onClear={() => setHqAddress('')} />
                                            </div>
                                        </div>
                                        <div className={`${classes.fieldWrap} ${classes.fieldFull}`}>
                                            <div className={classes.fieldLabel}>About Us <ConfBadge fields={fields} fieldKey="about_us" /></div>
                                            <div className={classes.aiFieldWrap} style={{ alignItems: 'flex-start' }}>
                                                <textarea className={`${classes.textInput}${wasAiFilled(fields, 'about_us') && aboutUs ? ` ${classes.aiClearInput}` : ''}`} value={aboutUs} onChange={e => setAboutUs(e.target.value)} rows={3} style={{ resize: 'vertical' }} placeholder="Brief company description" />
                                                <AiInputClear show={wasAiFilled(fields, 'about_us') && !!aboutUs} onClear={() => setAboutUs('')} />
                                            </div>
                                        </div>
                                        <div className={`${classes.fieldWrap} ${classes.fieldFull}`}>
                                            <div className={classes.fieldLabel}>Business Domains (comma-separated) <ConfBadge fields={fields} fieldKey="domain_business" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'domain_business') && domainBusiness ? ` ${classes.aiClearInput}` : ''}`} value={domainBusiness} onChange={e => setDomainBusiness(e.target.value)} placeholder="e.g. Primary Transport, Warehousing" />
                                                <AiInputClear show={wasAiFilled(fields, 'domain_business') && !!domainBusiness} onClear={() => setDomainBusiness('')} />
                                            </div>
                                        </div>
                                    </div>
                                </SectionToggle>

                                {/* Operations & Legal */}
                                <SectionToggle title="Operations & Legal" sKey="operations" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiOperations}>
                                    <div className={classes.fieldGrid}>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>PAN Number <ConfBadge fields={fields} fieldKey="pan_number" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'pan_number') && panNumber ? ` ${classes.aiClearInput}` : ''}`} value={panNumber} onChange={e => setPanNumber(e.target.value)} placeholder="AAAAA0000A" />
                                                <AiInputClear show={wasAiFilled(fields, 'pan_number') && !!panNumber} onClear={() => setPanNumber('')} />
                                            </div>
                                        </div>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>CIN Number <ConfBadge fields={fields} fieldKey="cin_number" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'cin_number') && cinNumber ? ` ${classes.aiClearInput}` : ''}`} value={cinNumber} onChange={e => setCinNumber(e.target.value)} placeholder="U12345AB2000PLC000000" />
                                                <AiInputClear show={wasAiFilled(fields, 'cin_number') && !!cinNumber} onClear={() => setCinNumber('')} />
                                            </div>
                                        </div>
                                    </div>
                                </SectionToggle>

                                {/* Fleet */}
                                <SectionToggle title="Fleet Strength" sKey="fleet" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiFleet}>
                                    <div className={classes.fieldGrid}>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>Owned Fleet (count) <ConfBadge fields={fields} fieldKey="owned_fleet" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'owned_fleet') && ownedFleet ? ` ${classes.aiClearInput}` : ''}`} value={ownedFleet} onChange={e => setOwnedFleet(e.target.value)} placeholder="e.g. 540" />
                                                <AiInputClear show={wasAiFilled(fields, 'owned_fleet') && !!ownedFleet} onClear={() => setOwnedFleet('')} />
                                            </div>
                                        </div>
                                        <div className={classes.fieldWrap}>
                                            <div className={classes.fieldLabel}>Associated Fleet (count) <ConfBadge fields={fields} fieldKey="associated_fleet" /></div>
                                            <div className={classes.aiFieldWrap}>
                                                <input className={`${classes.textInput}${wasAiFilled(fields, 'associated_fleet') && associatedFleet ? ` ${classes.aiClearInput}` : ''}`} value={associatedFleet} onChange={e => setAssociatedFleet(e.target.value)} placeholder="e.g. 200" />
                                                <AiInputClear show={wasAiFilled(fields, 'associated_fleet') && !!associatedFleet} onClear={() => setAssociatedFleet('')} />
                                            </div>
                                        </div>
                                        {/* <div className={`${classes.fieldWrap} ${classes.fieldFull}`}>
                                            <div className={classes.fieldLabel}>Owned Fleet — Vehicle Types</div>
                                            <input className={classes.textInput} value={ownedFleetList} onChange={e => setOwnedFleetList(e.target.value)} placeholder="Tankers, Trailers, Containers..." />
                                        </div> */}
                                    </div>
                                </SectionToggle>

                                {/* Financials */}
                                <SectionToggle title="Financials" sKey="financials" openSections={openSections} toggleSection={toggleSection} hasAiFilled={aiFinancials}>
                                    <div className={classes.reviewSubLabel} style={{ marginBottom: 6 }}>Turnover</div>
                                    {turnover.map((entry, i) => (
                                        <FinRow key={i} label={`FY ${i + 1}`} entry={entry} fyOptions={fyOptions}
                                            setEntry={upd => setTurnover(prev => prev.map((t, idx) => idx === i ? (typeof upd === 'function' ? upd(t) : upd) : t))} />
                                    ))}
                                    <div className={classes.reviewSubLabel} style={{ marginBottom: 6, marginTop: 10 }}>Net Profit</div>
                                    {netProfit.map((entry, i) => (
                                        <FinRow key={i} label={`FY ${i + 1}`} entry={entry} fyOptions={fyOptions}
                                            setEntry={upd => setNetProfit(prev => prev.map((t, idx) => idx === i ? (typeof upd === 'function' ? upd(t) : upd) : t))} />
                                    ))}
                                </SectionToggle>

                                {/* Additional Contacts — mandatory if partially filled */}
                                {extraContacts.length > 0 && (
                                    <div style={{ marginTop: 12 }} ref={extraContactsRef}>
                                        <div className={classes.reviewSubLabel} style={{ marginBottom: 6 }}>
                                            Additional Extracted Contacts
                                            <span style={{ color: '#9390c0', fontSize: 10, fontWeight: 400, marginLeft: 4 }}>(name, email & phone required if retaining)</span>
                                        </div>
                                        {extraContacts.map((c, i) => {
                                            const rowErr = contactErrors[i] || {};
                                            return (
                                                <div
                                                    key={i}
                                                    className={classes.reviewExtraContactRow}
                                                    ref={el => { extraContactRowRefs.current[i] = el; }}
                                                >
                                                    <div className={classes.fieldGrid} style={{ flex: 1 }}>
                                                        <div className={classes.fieldWrap}>
                                                            <div className={classes.fieldLabel}><span className={classes.asterisk}>*</span>Name</div>
                                                            <input className={`${classes.textInput} ${rowErr.name ? classes.invalid : ''}`} value={c.name}
                                                                onChange={e => { setExtraContacts(prev => prev.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x)); setContactErrors(prev => prev.map((er, ei) => ei === i ? { ...er, name: undefined } : er)); }}
                                                                placeholder="Full name" />
                                                            {rowErr.name && <div className={classes.errorMsg}>{rowErr.name}</div>}
                                                        </div>
                                                        <div className={classes.fieldWrap}>
                                                            <div className={classes.fieldLabel}><span className={classes.asterisk}>*</span>Email</div>
                                                            <input className={`${classes.textInput} ${rowErr.email ? classes.invalid : ''}`} value={c.email}
                                                                onChange={e => { setExtraContacts(prev => prev.map((x, xi) => xi === i ? { ...x, email: e.target.value } : x)); setContactErrors(prev => prev.map((er, ei) => ei === i ? { ...er, email: undefined } : er)); }}
                                                                placeholder="email@co.com" type="email" />
                                                            {rowErr.email && <div className={classes.errorMsg}>{rowErr.email}</div>}
                                                        </div>
                                                        <div className={classes.fieldWrap}>
                                                            <div className={classes.fieldLabel}><span className={classes.asterisk}>*</span>Phone</div>
                                                            <input className={`${classes.textInput} ${rowErr.contact_number ? classes.invalid : ''}`} value={c.contact_number}
                                                                onChange={e => { setExtraContacts(prev => prev.map((x, xi) => xi === i ? { ...x, contact_number: e.target.value } : x)); setContactErrors(prev => prev.map((er, ei) => ei === i ? { ...er, contact_number: undefined } : er)); }}
                                                                placeholder="+91 9XXXXXXXX" />
                                                            {rowErr.contact_number && <div className={classes.errorMsg}>{rowErr.contact_number}</div>}
                                                        </div>
                                                        <div className={classes.fieldWrap}>
                                                            <div className={classes.fieldLabel}>Designation</div>
                                                            <input className={classes.textInput} value={c.designation}
                                                                onChange={e => setExtraContacts(prev => prev.map((x, xi) => xi === i ? { ...x, designation: e.target.value } : x))}
                                                                placeholder="e.g. Manager" />
                                                        </div>
                                                    </div>
                                                    <button className={classes.truckRowRemove} onClick={() => { setExtraContacts(prev => prev.filter((_, xi) => xi !== i)); setContactErrors(prev => prev.filter((_, ei) => ei !== i)); extraContactRowRefs.current.splice(i, 1); }} type="button" style={{ marginTop: 18 }}>
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {saveErr && <div className={classes.uploadError} style={{ marginTop: 12 }}>{saveErr}</div>}
                            </>
                        )}

                    </div>{/* end reviewBody */}

                    {/* ── Footer buttons ── */}
                    <div className={classes.reviewStickyFooter}>
                        {reviewStep === 0 ? (
                            <>
                                <button className={classes.btnSecondary} onClick={() => { setReviewStep(0); reviewBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={saving}>← Back</button>
                                <button className={classes.btnPrimary} onClick={handleNext} id="onboarding-review-next">
                                    Next: Review Extracted Info →
                                </button>
                            </>
                        ) : (
                            <>
                                <button className={classes.btnSecondary} onClick={() => setReviewStep(0)} disabled={saving}>← Back</button>
                                <button className={classes.btnPrimary} onClick={handleSubmit} disabled={saving} id="onboarding-review-submit">
                                    {saving ? 'Saving…' : 'Confirm & Create Profile →'}
                                </button>
                            </>
                        )}
                    </div>

                    <div className={classes.centerText}>
                        <button className={classes.linkBtn} onClick={onSwitchPath}>Start over with manual form</button>
                    </div>

                </div>{/* end rightPanel */}
            </div>{/* end splitCard */}
        </div>
    );
};

export default OnboardingReview;